import { faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormControl from '@mui/material/FormControl';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ApiContextProps } from '../Contexts/ApiContext';
import ApiContext from '../Contexts/ApiContext';
import type { LocalStorageContextProps } from '../Contexts/LocalStorageContext';
import LocalStorageContext from '../Contexts/LocalStorageContext';
import type { ModeContextProps } from '../Contexts/ModeContext';
import ModeContext from '../Contexts/ModeContext';
import type { UserContextProps } from '../Contexts/UserContext';
import UserContext from '../Contexts/UserContext';
import Mode from '../Enums/Mode';
import type Token from '../Models/TokenModel';
import type { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import AppTheme from '../Enums/AppTheme';
import type { SettingsContextProps } from '../Contexts/SettingsContext';
import SettingsContext from '../Contexts/SettingsContext';
import Switch from '@mui/material/Switch';
import type { JSX } from 'react';
import { formFocusNextKeyboard, shakeForm } from 'shouldve-been-react';
import { PasswordInput, LoadingButton, ErrorMessage } from 'shouldve-been-mui';
import Typography from '@mui/material/Typography';
import KFleetLogo from '../Assets/Svg/KFleetLogo';
import { updateLastActivity } from '../Utils/LastActivity';
import CurrentUser from '../Models/CurrentUserModel';
import { appVersion } from '../Constants';
import type { TokenContextProps } from '../Contexts/TokenContext';
import TokenContext from '../Contexts/TokenContext';
import type Tenant from '../Models/TenantModel';
import type { SelectChangeEvent } from '@mui/material';
import { Button, MenuItem, Select } from '@mui/material';
import ApiService from '../Services/Api/ApiService';

function LoginPage(): JSX.Element {

    const userContext: UserContextProps = useContext<UserContextProps>(UserContext);
    const modeContext: ModeContextProps = useContext<ModeContextProps>(ModeContext);
    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);

    const currentTheme: Theme = useTheme();

    const [connectMode, setConnectMode] = useState<'step1' | 'step2'>('step1');

    //for step1
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [step1ConnectPending, setStep1ConnectPending] = useState<boolean>(false);

    //for step2
    const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
    const [tenantId, setTenantId] = useState<string>('');
    const [step2ConnectPending, setStep2ConnectPending] = useState<boolean>(false);

    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const formRef: React.RefObject<HTMLFormElement> = useRef<HTMLFormElement>(null);

    const tenantMenuItems: JSX.Element[] = useMemo<JSX.Element[]>(() => {

        return availableTenants.map((tenant: Tenant) => {
            return (
                <MenuItem 
                    key={tenant.tenantId}
                    value={tenant.tenantId}
                >
                    {tenant.displayName}
                </MenuItem> 
            );
        });
    }, [availableTenants]);

    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    function handleKeyboardSubmitStep1(e: React.KeyboardEvent): void {

        if (e.key === 'Enter') {
            e.preventDefault();
            void handleSubmitStep1();
        }
    }

    async function handleSubmitStep1(): Promise<void> {

        setStep1ConnectPending(true);

        const tenants: Tenant[] = await ApiService.getTenantsByEmail(email);

        if (tenants.length === 0) {
            setErrorMessage('Email is not connected to any tenants');
            shakeForm(formRef);
        } else if (tenants.length === 1) {
            await handleConnect(tenants[0]?.tenantId ?? '');
        } else {
            setAvailableTenants(tenants);
            setConnectMode('step2');
            setErrorMessage(null);
        }

        setStep1ConnectPending(false);
    }

    async function handleSubmitStep2(): Promise<void> {

        setStep2ConnectPending(true);

        await handleConnect(tenantId);

        setStep2ConnectPending(false);
    }

    async function handleConnect(currentTenantId: string): Promise<void> {

        const tokenInfo: Token | null = await apiContext.api?.fetchNewToken(email, password, currentTenantId) ?? null;
    
        const token: string | null = tokenInfo?.token ?? null;
        const expiration: Date | null = tokenInfo?.expiration ?? null;
        const customerId: number | null = tokenInfo?.customerId ?? null;
        const access: string[] | null = tokenInfo?.access ?? null;
    
        if (token === null || expiration === null || customerId === null || access === null) {
    
            setErrorMessage('Could not login');    
            shakeForm(formRef);
            return;
        }
    
        const user: CurrentUser = new CurrentUser(email, access);
    
        userContext.setCurrentUser(user);
        localStorageContext.localStorageService.setCurrentUser(user);
        updateLastActivity(userContext, localStorageContext);
        
        modeContext.setMode(Mode.MAIN);
        localStorageContext.localStorageService.setMode(Mode.MAIN);

        tokenContext.setToken(token);
        tokenContext.setExpiration(expiration);
        localStorageContext.localStorageService.setToken(token);
        localStorageContext.localStorageService.setExpiration(expiration);
    }

    function handleBack(): void {

        setAvailableTenants([]);
        setConnectMode('step1');
        setErrorMessage(null);
    }

    function updateTheme(theme: AppTheme): void {

        settingsContext.setTheme(theme);
        localStorageContext.localStorageService?.setTheme(theme);
    }

    return (
        <div style={{
            padding: '5px',
            height: windowSize[1],
            overflow: 'auto',
        }}>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '10px 10px 0px 10px',
            }}>
                <div style={{
                    textAlign: 'center',
                }}>
                    <Typography
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        Version
                    </Typography>
                    <Typography
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        {appVersion}
                    </Typography>
                </div>
                <div style={{
                    textAlign: 'right',
                }}>
                    <a
                        href='/LICENSE.md'
                        target='_blank'
                        rel='noreferrer'
                        style={{
                            color: currentTheme.palette.text.primary,
                        }}
                    >
                        Licenses
                    </a>
                    <table style={{
                        marginLeft: 'auto',
                    }}>
                        <tbody>
                            <tr>
                                <td style={{
                                    color: currentTheme.palette.text.primary,
                                }}>
                                    Dark Mode
                                </td>
                                <td>
                                    <FormControl>
                                        <Switch
                                            checked={settingsContext.theme === AppTheme.DARK_MODE}
                                            onChange={(e: React.ChangeEvent): void => updateTheme((e.target as HTMLInputElement).checked ? AppTheme.DARK_MODE : AppTheme.LIGHT_MODE)}
                                            color='primary'
                                        />           
                                    </FormControl>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <div 
                style={{
                    textAlign: 'center',
                    marginTop: '30px',
                }}
            >
                <KFleetLogo
                    color={currentTheme.palette.text.primary}
                    height={100}
                />
                <Typography
                    variant='h5'
                    style={{
                        color: currentTheme.palette.text.primary,
                        fontFamily: 'Permanent Marker',
                        marginBottom: '20px',
                    }}    
                >
                    K-Fleet Vessel Manager
                </Typography>
                <form noValidate autoComplete='off' ref={formRef} name='connect'>
                    {connectMode === 'step1' && 
                        <>
                            <br />
                            <FormControl style={{
                                minWidth: Math.min(windowSize[0] * 0.8, 300),
                                maxWidth: Math.min(windowSize[0] * 0.8, 300),
                            }}>
                                <InputLabel>Email</InputLabel>
                                <Input 
                                    value={email}
                                    onChange={(e: React.ChangeEvent): void => setEmail((e.target as HTMLInputElement).value)}
                                    onKeyDown={formFocusNextKeyboard}
                                    margin='dense'
                                    startAdornment={
                                        <InputAdornment position='start'>
                                            <FontAwesomeIcon icon={faUser} />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                            <br />
                            <br />
                            <PasswordInput 
                                password={password} 
                                onPasswordChange={setPassword}
                                onKeyDown={handleKeyboardSubmitStep1}
                                minWidth={Math.min(windowSize[0] * 0.8, 300)}
                                maxWidth={Math.min(windowSize[0] * 0.8, 300)}
                            />
                            <br />
                            <br />
                            <LoadingButton 
                                width='150px'
                                height='40px'
                                text='Login'
                                controller={step1ConnectPending}
                                onClick={handleSubmitStep1}
                                buttonColor='primary'
                                circularColor='error'
                                circularSize='30px'
                                variant='contained'
                            />
                        </>
                    }
                    {connectMode === 'step2' && 
                        <>
                            <FormControl 
                                size='small'
                                style={{
                                    width: Math.min(windowSize[0] * 0.8, 500),
                                    marginTop: '40px',
                                    textAlign: 'left',
                                }}
                            >
                                <InputLabel>Select Tenant</InputLabel>
                                <Select
                                    value={tenantId}
                                    onChange={(e: SelectChangeEvent): void => setTenantId((e.target as HTMLInputElement).value)}
                                    size='small'
                                    variant='outlined'
                                    label='Select Remote Tunnel'
                                >
                                    {tenantMenuItems}
                                </Select>
                            </FormControl>
                            <br />
                            <br />
                            <LoadingButton 
                                width='150px'
                                height='40px'
                                text='Login'
                                controller={step2ConnectPending}
                                onClick={handleSubmitStep2}
                                buttonColor='primary'
                                circularColor='error'
                                circularSize='30px'
                                variant='contained'
                            />
                            <br />
                            <br />
                            <Button 
                                onClick={handleBack}
                                color='error'
                                variant='contained'
                                style={{
                                    minWidth: '120px',
                                    maxWidth: '120px',
                                    minHeight: '32px',
                                    maxHeight: '32px',
                                }}
                            >
                                Back
                            </Button>
                        </>
                    }
                </form>
                <br />
                <ErrorMessage
                    message={errorMessage}
                    maxWidth={windowSize[0]}
                />
            </div>
        </div>
    );
}

export default LoginPage;